import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

import SignInForm from "@/components/sign-in-form";
import SignUpForm from "@/components/sign-up-form";
import { orpc } from "@/utils/orpc";
import logo from "@/assets/logo.png";
import * as m from "@/paraglide/messages.js";
import { LanguageToggle } from "@/components/language-toggle";

export const Route = createFileRoute("/login")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    const session = context.session;
    if (session && session.data) {
      if (session.data.user.role === "admin") {
        throw redirect({
          to: "/superadmin",
        });
      }
      throw redirect({
        to: "/dashboard",
      });
    }
  },
});

import { useTerminalStore } from "@/store/terminal";

function RouteComponent() {
  const [showSignIn, setShowSignIn] = useState(true);

  const { restaurantCode, restaurantName, setRestaurant } = useTerminalStore();

  const { isDesktop } = Route.useRouteContext();

  const [activeScreen, setActiveScreen] = useState<"setup" | "auth">(() => {
    if (isDesktop && !restaurantCode) {
      return "setup";
    }
    return "auth";
  });

  const handleActivate = (code: string, name: string) => {
    setRestaurant(code, name);
    setActiveScreen("auth");
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-[#90cbf9] via-[#e3f2fd] to-white p-4 select-none">
      <div className="absolute top-4 right-4 z-50">
        <LanguageToggle />
      </div>

      {/* Background Concentric Rings (SVG) */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <svg
          className="w-[160%] h-[160%] max-w-[1400px] max-h-[1400px] text-white opacity-40"
          viewBox="0 0 800 800"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="400"
            cy="400"
            r="180"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeDasharray="3 3"
          />
          <circle cx="400" cy="400" r="280" stroke="currentColor" strokeWidth="1.2" />
          <circle
            cx="400"
            cy="400"
            r="390"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeDasharray="4 4"
          />
          <circle cx="400" cy="400" r="500" stroke="currentColor" strokeWidth="1.2" />
          <circle cx="400" cy="400" r="620" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </div>

      {/* Blurred Cloud Ellipses at the Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-2/5 z-0 pointer-events-none overflow-hidden">
        <div className="absolute -bottom-24 -left-[10%] w-[65%] h-[350px] rounded-full bg-white/40 blur-3xl" />
        <div className="absolute -bottom-32 left-[15%] w-[70%] h-[380px] rounded-full bg-white/50 blur-3xl" />
        <div className="absolute -bottom-20 -right-[5%] w-[55%] h-[300px] rounded-full bg-white/30 blur-3xl" />
      </div>

      {/* Glassmorphic Container Card */}
      <div className="relative z-10 w-full max-w-[440px] rounded-[32px] border border-white/60 bg-white/60 p-8 shadow-[0_24px_60px_rgba(0,0,0,0.06)] backdrop-blur-xl md:p-10 transition-all duration-300 animate-in fade-in zoom-in-95 duration-200">
        {activeScreen === "setup" ? (
          <TerminalActivationForm onActivate={handleActivate} />
        ) : (
          <div className="relative">
            {showSignIn ? (
              <SignInForm onSwitchToSignUp={() => setShowSignIn(false)} />
            ) : (
              <SignUpForm onSwitchToSignIn={() => setShowSignIn(true)} />
            )}

            {isDesktop && restaurantCode && (
              <div className="mt-8 pt-4 border-t border-gray-200/50 flex items-center text-[10px] font-bold text-gray-400 uppercase select-none">
                <span>Terminal: {restaurantName || restaurantCode}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Client Mode Indicator for verification */}
      <div className="mt-4 px-3 py-1.5 rounded-full bg-white/40 border border-white/60 text-[10px] font-bold tracking-wide uppercase text-gray-500 shadow-sm relative z-10 select-none">
        {m.auth_platform()}: {isDesktop ? m.auth_desktop() : m.auth_webBrowser()}
      </div>
    </div>
  );
}

interface TerminalActivationFormProps {
  onActivate: (code: string, name: string) => void;
}

function TerminalActivationForm({ onActivate }: TerminalActivationFormProps) {
  const { deviceId, userCode, setUserCode } = useTerminalStore();

  // Helper to generate 6-character user code (excluding similar characters)
  const [activeCode] = useState<string>(() => {
    if (userCode) return userCode;
    const charset = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return result;
  });

  // Sync with store
  useEffect(() => {
    if (!userCode) {
      setUserCode(activeCode);
    }
  }, [userCode, activeCode, setUserCode]);

  // Call initTerminalSession to register the pending code exactly once
  const initSessionQuery = useQuery({
    ...orpc.restaurant.initTerminalSession.queryOptions({
      input: {
        deviceId,
        userCode: activeCode,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      },
    }),
    enabled: !!deviceId && !!activeCode,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Poll checkTerminalStatus every 5s until active is true
  const { data: statusData } = useQuery({
    ...orpc.checkTerminalStatus.queryOptions({
      input: { deviceId },
    }),
    enabled: !!deviceId && !!activeCode,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.active ? false : 5000;
    },
  });

  // Trigger onActivate when terminal becomes active
  useEffect(() => {
    if (statusData?.active && statusData.restaurantCode && statusData.restaurantName) {
      toast.success(m.auth_activationSuccess({ name: statusData.restaurantName }));
      onActivate(statusData.restaurantCode, statusData.restaurantName);
    }
  }, [statusData, onActivate]);

  // Format code to show a dash in the middle, e.g., XY9-7T1
  const formatCode = (raw: string) => {
    if (raw.length < 6) return raw;
    return `${raw.slice(0, 3)}-${raw.slice(3)}`;
  };

  const errorMsg = initSessionQuery.isError ? m.auth_serverConnError() : null;

  return (
    <div className="w-full flex flex-col">
      <div className="flex justify-center items-center gap-2 mb-6 select-none">
        <img src={logo} alt="Vidyon Logo" className="h-8 w-auto object-contain" />
        <div className="h-5 w-px bg-gray-300" />
        <span className="text-lg font-bold tracking-tight text-gray-900">POS</span>
      </div>

      <h2 className="text-2xl font-bold text-center text-gray-905 tracking-tight">
        {m.auth_deviceActivation()}
      </h2>
      <p className="mt-2 text-sm text-slate-500 text-center leading-relaxed max-w-[320px] mx-auto mb-6">
        {m.auth_activationInstruction()}
      </p>

      {errorMsg ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-xs font-semibold text-red-700 shadow-sm animate-in fade-in duration-200">
          {errorMsg}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-3xl border border-blue-100/70 shadow-sm relative overflow-hidden group select-all">
            <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider mb-2 select-none">
              {m.auth_activationCode()}
            </span>
            <span className="text-3xl font-black tracking-widest text-center text-blue-600 ">
              {formatCode(activeCode)}
            </span>
          </div>

          <div className="flex items-center justify-center gap-2.5 py-1 select-none">
            <Loader2 className="size-4 animate-spin text-blue-500" />
            <span className="text-xs text-gray-400 font-bold tracking-wide uppercase">
              {m.auth_waitingAdmin()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
